import React, { useContext } from "react"
import { observer } from "mobx-react"
import { Button, Group, Text } from "@mantine/core"
import { IconArrowRight, IconExternalLink } from "@tabler/icons-react"
import { AppWorkspaceContext } from "../../stores/AppWorkspace"
import { LANDING_COPY } from "../../../docs/catalog"
import type { Platform } from "../../../docs/types"
import { CopyPromptButton } from "./CopyPromptButton"
import { TestedBadge } from "./TestedBadge"

// One platforms[] row as a card, on the homepage and on /platforms: logo,
// label, summary, "Runs on" plus the framework, the badge, and up to three
// actions — Get started (the page), the starter's own button, Copy prompt. A
// planned row has no page, so it is drawn as "Coming next" with no link and
// no actions: it is never presented as available.
export const PlatformCard: React.FC<{ platform: Platform }> = observer(({ platform }) => {
  const workspace = useContext(AppWorkspaceContext)
  const logo = workspace.platformLogoUrl(platform.id)
  const path = platform.path

  // Every platform mark is a wordmark that spells the name, so a card with a
  // logo shows the logo alone (named by its alt text); a planned row has no
  // logo and shows its name.
  const head = (
    <>
      {logo ? (
        <span className="or-framework-logo or-platform-logo" data-platform={platform.id}>
          <img src={logo} alt={platform.label} />
        </span>
      ) : (
        <Text className="or-framework-card-title">{platform.label}</Text>
      )}
      <Text className="or-framework-card-pitch">{platform.summary}</Text>
      {platform.frameworkLabel ? (
        <Text className="or-framework-card-requires">Runs on {platform.frameworkLabel}</Text>
      ) : null}
    </>
  )

  if (!path) {
    return (
      <li className="or-framework-card or-platform-card is-planned">
        <div className="or-framework-card-link">{head}</div>
        <span className="or-coming-next">{LANDING_COPY.platforms.comingNext}</span>
      </li>
    )
  }

  return (
    <li className="or-framework-card or-platform-card">
      <a
        className="or-framework-card-link"
        href={path}
        onClick={(event) => {
          event.preventDefault()
          workspace.goPlatforms(platform.id)
        }}
      >
        {head}
      </a>
      <TestedBadge tested={platform.tested} />
      <Group className="or-framework-card-actions" gap="xs" wrap="wrap">
        <Button
          component="a"
          href={path}
          size="sm"
          rightSection={<IconArrowRight size={16} />}
          onClick={(event) => {
            event.preventDefault()
            workspace.goPlatforms(platform.id)
          }}
        >
          Get started
        </Button>
        {platform.starter ? (
          <Button
            component="a"
            href={platform.starter.url}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            variant="default"
            rightSection={<IconExternalLink size={14} />}
          >
            {platform.starter.label}
          </Button>
        ) : null}
        {platform.prompt ? <CopyPromptButton prompt={platform.prompt} platformId={platform.id} label={platform.label} /> : null}
      </Group>
    </li>
  )
})
